import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';

const EASE = 'cubic-bezier(.4, 0, .2, 1)';
const DURATION_FAST = '140ms';
const DURATION_MEDIUM = '220ms';

// Fully rounded ends, which the 8px default cannot express.
const PILL_RADIUS = 999;

// Tighter than the md default: three tabs, a limits pill and three controls share one strip, and
// the 16px default is what pushed the controls onto a second row on a laptop.
const TAB_PADDING_X = 12;

// The pill gives up its summary before the strip wraps, but not its own chrome — below this it
// would shrink into the control beside it, so the strip wraps instead.
const LIMITS_PILL_MIN_WIDTH = 150;
const LIMITS_PILL_HEIGHT = 28;

const LOGO_SIZE = 16;
const STATUS_DOT_SIZE = 6;

const fadeSlideIn = {
	from: { opacity: 0, transform: 'translateY(-4px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
};

const popIn = {
	from: { opacity: 0, transform: 'scale(.4)' },
	to: { opacity: 1, transform: 'scale(1)' },
};

const enters = {
	animationDuration: DURATION_MEDIUM,
	animationName: [fadeSlideIn],
	animationTimingFunction: EASE,
};

const focusRing = {
	outline: `2px solid ${THEME.colors.primary.main}`,
	outlineOffset: 2,
};

export const styleSheet = StyleSheet.create({
	// The wrapper supplies the one-pixel border while the standard small button supplies its type and spacing.
	toggleBeam: {
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		display: 'inline-flex',
		flexShrink: 0,
		overflow: 'hidden',
		':has(button:focus-visible)': {
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	toggleButton: {
		backgroundColor: THEME.colors.background.light,
		borderRadius: THEME.common.borderRadius - 1,
		boxShadow: 'none',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		height: 30,
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
		':focus-visible': {
			outline: 'none',
		},
	},
	toggleTrack: {
		backgroundColor: THEME.colors.border.main,
		borderRadius: PILL_RADIUS,
		flexShrink: 0,
		height: 18,
		position: 'relative',
		transition: `background-color ${DURATION_MEDIUM} ${EASE}`,
		width: 30,
	},
	toggleTrackActive: {
		backgroundColor: THEME.colors.primary.main,
	},
	toggleThumb: {
		backgroundColor: THEME.colors.background.light,
		borderRadius: PILL_RADIUS,
		height: 12,
		left: 3,
		position: 'absolute',
		top: 3,
		transition: `transform ${DURATION_MEDIUM} ${EASE}`,
		width: 12,
	},
	toggleThumbActive: {
		transform: 'translateX(12px)',
	},

	// Once the post is split this rule is also the panel's top edge: it runs the panel's full width,
	// and the selected tab's stroke paints over its own slice of it.
	versionBar: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: `${SPACE[4]}px ${SPACE[8]}px`,
		justifyContent: 'space-between',
	},
	// Reversed, so a strip too narrow for one row puts the controls above the tabs. The rule is the
	// panel's top edge: the tabs have to stay on the line that carries it, or the open tab detaches
	// from the panel it names.
	versionBarSplit: {
		borderBottom: `1px solid ${THEME.colors.border.main}`,
		flexWrap: 'wrap-reverse',
	},
	// The tabs set the row's height, so the pill and the controls center on the tab labels instead of
	// padding themselves to a height of their own.
	versionBarSplitSlot: {
		paddingBottom: 0,
		paddingTop: 0,
	},
	// A branding run puts its own controls in this slot, and they need the strip's whole width to sit
	// opposite the platform controls instead of queueing up behind them on the right.
	versionBarActionsFull: {
		flexBasis: '100%',
	},
	versionBarActions: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		flexWrap: 'wrap',
		gap: SPACE[8],
		justifyContent: 'flex-end',
		marginLeft: 'auto',
		// Clears the rule the tabs sit on, so the controls never look attached to the panel.
		paddingBottom: SPACE[8],
		paddingTop: SPACE[8],
	},

	// Takes the slack the lead and the actions leave, and gives it up first when the strip is tight.
	// A zero basis is what makes that work: the slot is never wide enough to push a wrap of its own,
	// so the pill inside truncates while the tabs and the controls keep their line.
	versionBarLimits: {
		alignItems: 'center',
		display: 'flex',
		flex: '1 1 0',
		minWidth: LIMITS_PILL_MIN_WIDTH,
		paddingBottom: SPACE[8],
		paddingTop: SPACE[8],
	},

	versionLead: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[8],
		minWidth: 0,
		paddingBottom: SPACE[8],
		paddingTop: SPACE[8],
	},
	versionLeadLogos: {
		alignItems: 'center',
		display: 'flex',
	},
	versionLeadLogo: {
		// The ring keeps each logo readable where the next one overlaps it.
		background: THEME.colors.background.light,
		border: `2px solid ${THEME.colors.background.light}`,
		borderRadius: PILL_RADIUS,
		boxSizing: 'content-box',
		display: 'block',
		flexShrink: 0,
		height: LOGO_SIZE,
		width: LOGO_SIZE,
		':not(:first-child)': {
			marginLeft: -8,
		},
	},

	// Stretched rather than pinned to flex-end: the split strip wraps in reverse, which turns flex-end
	// into the top of the row and lifts the tabs off the rule.
	tabBar: {
		alignItems: 'flex-end',
		alignSelf: 'stretch',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[4],
		// Overlaps the bar's rule, so the selected tab's stroke lands on it rather than above it.
		marginBottom: -1,
		minWidth: 0,
	},
	// Square, with the stroke under the open tab, as in the app's other horizontal tabs.
	tab: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		borderRadius: 0,
		color: THEME.colors.text.light,
		cursor: 'pointer',
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
		padding: `${SPACE[12]}px ${TAB_PADDING_X}px`,
		position: 'relative',
		transition: `color ${DURATION_FAST} ${EASE}, background-color ${DURATION_FAST} ${EASE}, box-shadow ${DURATION_FAST} ${EASE}`,
		whiteSpace: 'nowrap',
		':hover': {
			backgroundColor: THEME.colors.background.main,
			color: THEME.colors.text.main,
		},
		// Sits inside: an outset ring would be cut off by the rule under the strip.
		':focus-visible': {
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: -3,
		},
	},
	tabSelected: {
		// Inset, so marking the open tab costs no layout.
		boxShadow: `inset 0 -2px 0 ${THEME.colors.primary.main}`,
		color: THEME.colors.primary.main,
		zIndex: 1,
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
	tabInvalid: {
		color: THEME.colors.danger.dark,
		':hover': {
			color: THEME.colors.danger.dark,
		},
	},
	tabSelectedInvalid: {
		boxShadow: `inset 0 -2px 0 ${THEME.colors.danger.main}`,
		color: THEME.colors.danger.dark,
		':hover': {
			color: THEME.colors.danger.dark,
		},
	},
	tabLogo: {
		display: 'block',
		flexShrink: 0,
		height: LOGO_SIZE,
		width: LOGO_SIZE,
	},
	tabLogoMuted: {
		opacity: 0.55,
		transition: `opacity ${DURATION_FAST} ${EASE}`,
	},
	tabDot: {
		animationDuration: '180ms',
		animationName: [popIn],
		animationTimingFunction: EASE,
		backgroundColor: THEME.colors.primary.main,
		borderRadius: PILL_RADIUS,
		flexShrink: 0,
		height: STATUS_DOT_SIZE,
		width: STATUS_DOT_SIZE,
	},
	tabDotNotice: {
		backgroundColor: THEME.colors.info.main,
	},
	tabDotInvalid: {
		backgroundColor: THEME.colors.danger.main,
	},
	srOnly: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		whiteSpace: 'nowrap',
		width: 1,
	},

	// A text link in the strip rather than a boxed button, so it drops the small size's height and padding.
	resetButton: {
		...enters,
		color: THEME.colors.primary.main,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
		height: 'auto',
		paddingLeft: 0,
		paddingRight: 0,
		':has(> svg)': {
			paddingLeft: 0,
			paddingRight: 0,
		},
		':has(*) > svg': {
			height: THEME.typography.fontSizes.xs,
			width: THEME.typography.fontSizes.xs,
		},
		':hover': {
			background: 'none',
			color: THEME.colors.hover.dark,
		},
		':focus-visible': focusRing,
	},

	errorPanel: {
		...enters,
		backgroundColor: THEME.colors.danger.light,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		padding: `${SPACE[8]}px ${SPACE[16]}px`,
	},
	errorPanelStandalone: {
		border: `1px solid ${THEME.colors.danger.main}`,
		borderRadius: THEME.common.borderRadius,
		marginBottom: SPACE[8],
	},
	errorRow: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[8],
		lineHeight: 1.4,
	},
	errorIcon: {
		display: 'block',
		flexShrink: 0,
	},

	handlesNotice: {
		...enters,
		backgroundColor: THEME.colors.info.light,
		border: `1px solid ${THEME.colors.info.main}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		display: 'flex',
		flexDirection: 'column',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
		lineHeight: 1.4,
		marginTop: SPACE[8],
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
	},
	handlesNoticeLinks: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: `${SPACE[4]}px ${SPACE[12]}px`,
	},
	handlesNoticeLink: {
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.bold,
		overflowWrap: 'anywhere',
		textDecoration: 'underline',
		':focus-visible': focusRing,
	},

	// A pill sized to sit on the version strip beside the lead sentence, so the limits stay in view
	// without taking a row of their own.
	limitsTrigger: {
		alignItems: 'center',
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: PILL_RADIUS,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		boxSizing: 'border-box',
		display: 'inline-flex',
		flexShrink: 1,
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
		height: LIMITS_PILL_HEIGHT,
		lineHeight: 1,
		maxWidth: '100%',
		minWidth: 0,
		padding: `0 ${SPACE[8]}px`,
		transition: `background-color ${DURATION_FAST} ${EASE}, border-color ${DURATION_FAST} ${EASE}`,
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
		':focus-visible': focusRing,
	},
	limitsTriggerLabel: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	// The first thing to give up room when the strip is tight: the table behind the pill still has it.
	limitsTriggerSummary: {
		color: THEME.colors.text.light,
		minWidth: 0,
		...THEME.common.truncate,
	},
	limitsChevron: {
		color: THEME.colors.text.light,
		display: 'block',
		flexShrink: 0,
		height: THEME.typography.fontSizes.md,
		transition: `transform ${DURATION_MEDIUM} ${EASE}`,
		width: THEME.typography.fontSizes.md,
	},
	limitsChevronOpen: {
		transform: 'rotate(180deg)',
	},
	limitsPopover: {
		maxHeight: 'min(70vh, 560px)',
		overflowY: 'auto',
		padding: SPACE[16],
		width: 'min(560px, calc(100vw - 32px))',
		...THEME.common.scrollBar,
	},
	limitsPopoverHeading: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		margin: `0 0 ${SPACE[8]}px`,
	},
	limitsScroll: {
		overflowX: 'auto',
		...THEME.common.scrollBar,
	},
	limitsTable: {
		// Separate, not collapsed: the combined column is rounded, and collapsed borders drop the radius.
		borderCollapse: 'separate',
		borderSpacing: 0,
		fontSize: THEME.typography.fontSizes.xs,
		minWidth: '100%',
		width: '100%',
	},
	limitsHeadCell: {
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		color: THEME.colors.text.light,
		fontWeight: THEME.typography.fontWeights.standard,
		padding: `${SPACE[8]}px ${SPACE[8]}px`,
		textAlign: 'right',
		verticalAlign: 'bottom',
		whiteSpace: 'nowrap',
	},
	limitsHeadCellLabel: {
		paddingLeft: 0,
		textAlign: 'left',
		width: '38%',
	},
	limitsPlatformHead: {
		alignItems: 'center',
		display: 'inline-flex',
		gap: SPACE[4],
		justifyContent: 'flex-end',
	},
	// One tinted column, unbroken from the header to the last row, so the reduction reads as a
	// column of the table rather than a stripe painted across some of its cells.
	limitsCombinedColumn: {
		background: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
	},
	limitsCombinedTop: {
		borderTopLeftRadius: THEME.common.borderRadius,
		borderTopRightRadius: THEME.common.borderRadius,
	},
	limitsCombinedBottom: {
		borderBottomLeftRadius: THEME.common.borderRadius,
		borderBottomRightRadius: THEME.common.borderRadius,
	},
	limitsActiveColumn: {
		background: THEME.colors.background.main,
		color: THEME.colors.text.main,
	},
	limitsGroupCell: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		letterSpacing: '.6px',
		padding: `${SPACE[16]}px 0 ${SPACE[4]}px`,
		textAlign: 'left',
		textTransform: 'uppercase',
	},
	limitsRow: {
		transition: `background-color ${DURATION_FAST} ${EASE}`,
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
	},
	limitsLabelCell: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.standard,
		padding: `${SPACE[8]}px 0`,
		textAlign: 'left',
	},
	limitsLabel: {
		display: 'inline-block',
	},
	// The label is the hover target for the reduction note, so it has to look like one without
	// costing every row an icon.
	limitsLabelHinted: {
		cursor: 'help',
		textDecoration: 'underline dotted',
		textDecorationColor: THEME.colors.border.main,
		textUnderlineOffset: 3,
	},
	limitsValueCell: {
		color: THEME.colors.text.main,
		padding: `${SPACE[8]}px`,
		textAlign: 'right',
		whiteSpace: 'nowrap',
	},
	limitsValueUnknown: {
		color: THEME.colors.text.light,
		cursor: 'help',
	},
	limitsValueBinding: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	limitsNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.5,
		marginTop: SPACE[16],
	},
	limitsTooltip: {
		maxWidth: 260,
	},

	discardDialogBody: {
		color: THEME.colors.text.main,
		display: 'flex',
		flexDirection: 'column',
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[8],
		lineHeight: 1.5,
	},
	discardDialogList: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},
	discardDialogItem: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[8],
	},
	discardDialogLogo: {
		display: 'block',
		flexShrink: 0,
		height: LOGO_SIZE,
		width: LOGO_SIZE,
	},
});
